import type { BackendExportFormat } from "@/lib/backend-export";
import type { ModuleType } from "@/lib/types";
import {
  PLAN_ORDER,
  STANDARD_AI_RUN_CREDITS,
  SUBSCRIPTION_TIERS,
  formatAiRunsFromCredits,
  type SubscriptionTier,
} from "@/lib/pricing";

export type Capability =
  | "generation"
  | "evaluation"
  | "refinement"
  | "prototype"
  | "product_context"
  | "project_context"
  | "context_extraction"
  | "context_qa"
  | "personas_private"
  | "personas_product"
  | "personas_org"
  | "comments"
  | "notifications"
  | "approval_workflow"
  | "advanced_approval"
  | "invite_members"
  | "manage_roles"
  | "product_permissions"
  | "billing_usage"
  | "export_markdown"
  | "export_csv"
  | "export_json"
  | "export_jira_csv"
  | "export_linear_csv"
  | "export_notion";

export interface EntitlementDecision {
  allowed: boolean;
  requiredTier?: SubscriptionTier | "enterprise";
  reason?: string;
  upgradeLabel?: string;
}

export const PLAN_RANK: Record<SubscriptionTier, number> = PLAN_ORDER.reduce(
  (acc, plan, index) => ({ ...acc, [plan]: index }),
  {} as Record<SubscriptionTier, number>,
);

export const CAPABILITY_MIN_TIER: Record<Capability, SubscriptionTier | "enterprise"> = {
  generation: "free",
  evaluation: "solo",
  refinement: "solo",
  prototype: "business",
  product_context: "free",
  project_context: "free",
  context_extraction: "startup",
  context_qa: "startup",
  personas_private: "free",
  personas_product: "startup",
  personas_org: "startup",
  comments: "solo",
  notifications: "solo",
  approval_workflow: "startup",
  advanced_approval: "business",
  invite_members: "startup",
  manage_roles: "startup",
  product_permissions: "startup",
  billing_usage: "free",
  export_markdown: "free",
  export_csv: "solo",
  export_json: "startup",
  export_jira_csv: "startup",
  export_linear_csv: "startup",
  export_notion: "solo",
};
const EXPORT_FORMAT_CAPABILITY: Record<BackendExportFormat, Capability> = {
  markdown: "export_markdown",
  csv: "export_csv",
  json: "export_json",
  jira_csv: "export_jira_csv",
  linear_csv: "export_linear_csv",
  notion_markdown: "export_notion",
  notion_csv: "export_notion",
};

const MODULE_CAPABILITY: Record<ModuleType, Capability> = {
  generation: "generation",
  evaluation: "evaluation",
  refinement: "refinement",
  prototype: "prototype",
};

export function planLabel(plan: SubscriptionTier | "enterprise"): string {
  if (plan === "enterprise") return "Enterprise";
  return SUBSCRIPTION_TIERS[plan].publicName || SUBSCRIPTION_TIERS[plan].name;
}

export function isPlanAtLeast(current: SubscriptionTier, required: SubscriptionTier | "enterprise"): boolean {
  if (required === "enterprise") return false;
  return PLAN_RANK[current] >= PLAN_RANK[required];
}

export function capabilityDecision(current: SubscriptionTier, capability: Capability): EntitlementDecision {
  const requiredTier = CAPABILITY_MIN_TIER[capability];
  const allowed = isPlanAtLeast(current, requiredTier);
  if (allowed) return { allowed };

  return {
    allowed: false,
    requiredTier,
    upgradeLabel: `${planLabel(requiredTier)}+`,
    reason: `${planLabel(requiredTier)} or higher required`,
  };
}

export function runCapabilityDecision(current: SubscriptionTier, moduleType: ModuleType): EntitlementDecision {
  return capabilityDecision(current, MODULE_CAPABILITY[moduleType]);
}

export function exportCapabilityDecision(current: SubscriptionTier, format: BackendExportFormat): EntitlementDecision {
  return capabilityDecision(current, EXPORT_FORMAT_CAPABILITY[format] ?? "export_json");
}

export function moduleRunCostCredits(moduleType: ModuleType): number {
  if (moduleType === "prototype") return STANDARD_AI_RUN_CREDITS * 3;
  return STANDARD_AI_RUN_CREDITS;
}

export function moduleRunCostLabel(moduleType: ModuleType): string {
  return formatAiRunsFromCredits(moduleRunCostCredits(moduleType));
}

export function availableCredits(balance: { totalRemaining?: number | null } | null | undefined): number {
  return Number(balance?.totalRemaining ?? 0);
}

export function hasEnoughCredits(balance: { totalRemaining?: number | null } | null | undefined, requiredCredits: number): boolean {
  return availableCredits(balance) >= requiredCredits;
}

export function usageDecision(
  balance: { totalRemaining?: number | null } | null | undefined,
  requiredCredits: number,
): EntitlementDecision {
  if (hasEnoughCredits(balance, requiredCredits)) return { allowed: true };
  const available = availableCredits(balance);
  return {
    allowed: false,
    reason: `Not enough AI runs available. Need ${formatAiRunsFromCredits(requiredCredits)}, have ${formatAiRunsFromCredits(available)}.`,
  };
}
